import { useIntl } from "react-intl";
import { AbstractTile } from "@/components/design/AbstractTile";
import { isRealUpdateTimestamp } from "@/lib/tobis-freshness";
import type { TobisTitle } from "@/lib/tobis";

// Kachel im Zuschnitt der SHARE-Karten (ShareItemCardFeed): box, aspect-[4/5],
// 60/40-Teilung, Badge oben links, Fusszeile unten. Eigene Komponente statt
// Wiederverwendung, weil ShareItemCardFeed an ShareItemLike haengt und sein
// Cover base64 aus `mainImage` erwartet, waehrend TVmaze Poster-URLs liefert.
//
// Der Typ-Badge steht bewusst NICHT in Grossbuchstaben: ADR-0026 reserviert
// All-Caps fuer die seitenuebergreifende Navigation.

// Nur coverUrl statt des ganzen TobisTitle: die Startseiten-Zeile arbeitet mit
// einer schmalen Projektion (TobisHomeTitle), und der Fallback soll nicht ein
// zweites Mal geschrieben werden muessen.
export function TobisTitleCover({
  title,
  index,
}: {
  title: Pick<TobisTitle, "coverUrl">;
  index: number;
}) {
  if (title.coverUrl) {
    return (
      <img
        src={title.coverUrl}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
    );
  }
  // Ohne Poster die generische Kachel, damit das Raster nicht einbricht.
  return (
    <AbstractTile
      variant={["dots", "lines", "grid", "rings"][index % 4] as "dots"}
      hue={(index * 47) % 360}
      className="absolute inset-0 h-full w-full"
    />
  );
}

/**
 * Der Zeitstempel des letzten Ingest-Kontakts, kurz genug fuer die Fusszeile.
 *
 * Mit Uhrzeit und nicht nur mit Datum: die Sortierung dient dazu, einen
 * einzelnen Import-Lauf wiederzufinden, und davon laufen mehrere am selben Tag.
 *
 * Titel ohne jeden API-Kontakt tragen 'epoch' (V0110) - fuer sie gibt es nichts
 * anzuzeigen, "01.01.1970" waere eine Zahl, die eine Aussage vortaeuscht.
 * Verglichen wird gegen 1990 statt exakt gegen den Epoch-Wert, damit auch eine
 * andere Sentinel-Wahl nicht versehentlich als echtes Datum durchgeht.
 */
function formatUpdatedAt(intl: ReturnType<typeof useIntl>, iso: string | undefined): string | null {
  if (!isRealUpdateTimestamp(iso)) return null;
  const d = new Date(iso as string);
  return intl.formatDate(d, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function TobisTitleCard({ title, index = 0 }: { title: TobisTitle; index?: number }) {
  const intl = useIntl();
  const year = title.year === null ? "" : String(title.year);
  const votes = title.imdbVotes ? Number(title.imdbVotes) : null;
  const updated = formatUpdatedAt(intl, title.updatedAt);

  return (
    <article className="box flex aspect-[4/5] flex-col overflow-hidden bg-surface">
      <div className="relative shrink-0 basis-[60%]">
        <TobisTitleCover title={title} index={index} />
        {title.kind ? (
          <span className="absolute left-2 top-2 rounded bg-accent px-1.5 py-0.5 text-[9px] tracking-widest text-on-accent">
            {title.kind}
          </span>
        ) : null}
        {/* Nur im Labor gesetzt (Tonalitaets-Rangierung). >0 statt truthy: 0
            ("TMDB-Treffer, keine Ueberlappung") soll kein Badge zeigen, null
            ("kein TMDB-Treffer") sowieso nicht. */}
        {title.keywordMatchCount !== null && title.keywordMatchCount > 0 ? (
          <span className="absolute right-2 top-2 rounded-full bg-ink/70 px-1.5 py-0.5 text-[9px] tracking-wide text-on-ink">
            {intl.formatMessage(
              { id: "tobis.card.keywordMatch", defaultMessage: "{count} keywords" },
              { count: title.keywordMatchCount },
            )}
          </span>
        ) : null}
        {/* Nur im Labor gesetzt (Regie/Drehbuchautor:in/Cast im Pitch). Eigene
            Ecke statt Stapel mit dem Keyword-Badge: beide Signale koennen
            gleichzeitig greifen, und die Personen-Ueberlappung ist laut
            listTitlesRankedByTonality das spezifischere Comp-Signal. */}
        {title.personMatchCount !== null && title.personMatchCount > 0 ? (
          <span className="absolute bottom-2 left-2 rounded-full bg-ink/70 px-1.5 py-0.5 text-[9px] tracking-wide text-on-ink">
            {intl.formatMessage(
              { id: "tobis.card.personMatch", defaultMessage: "{count} cast/crew" },
              { count: title.personMatchCount },
            )}
          </span>
        ) : null}
      </div>

      <div className="flex min-h-0 basis-[40%] flex-col p-3">
        <div className="min-h-0">
          <div className="line-clamp-2 text-sm font-medium leading-tight">{title.name}</div>
          {title.summary ? (
            <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-ink/55">{title.summary}</p>
          ) : null}
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-2 text-[12px] text-ink/45">
          <span className="min-w-0 truncate text-ink/40">
            {[
              // Vorangestellt und nicht angehaengt: es ist nur bei der
              // Aktualitaets-Sortierung gesetzt, und dann ist es die Angabe,
              // wegen der die Liste so sortiert wurde - sie darf nicht als
              // erstes weggekuerzt werden.
              updated,
              year,
              title.genres[0],
              // Nur angehaengt, nicht als eigene Zeile: die Karte hat eine feste
              // Hoehe (aspect-[4/5], 40% Infozone), ein zusaetzliches Element
              // koennte bei langer Summary ueberlaufen. Der bestehende
              // truncate-Span faengt das ab.
              title.admissionsDe
                ? intl.formatMessage(
                    { id: "tobis.card.admissionsDe", defaultMessage: "{count} DE" },
                    { count: intl.formatNumber(Number(title.admissionsDe), { notation: "compact" }) },
                  )
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <span className="shrink-0 tabular-nums">
            {title.imdbRating
              ? intl.formatMessage(
                  { id: "tobis.card.rating", defaultMessage: "{rating} ({votes})" },
                  {
                    rating: title.imdbRating,
                    votes: votes === null ? "" : intl.formatNumber(votes, { notation: "compact" }),
                  },
                )
              : intl.formatMessage({ id: "tobis.card.noRating", defaultMessage: "no rating" })}
          </span>
        </div>
      </div>
    </article>
  );
}
